"use strict";
/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapStateToArrayLayoutProps = exports.mapStateToOneOfProps = exports.mapStateToAnyOfProps = exports.mapStateToAllOfProps = exports.controlDefaultProps = exports.mapStateToJsonFormsRendererProps = exports.mapStateToLayoutProps = exports.layoutDefaultProps = exports.mapDispatchToMultiEnumProps = exports.mapDispatchToArrayControlProps = exports.mapStateToArrayControlProps = exports.mapStateToControlWithDetailProps = exports.mapStateToMasterListItemProps = exports.mapStateToMultiEnumControlProps = exports.mapStateToOneOfEnumControlProps = exports.mapStateToEnumControlProps = exports.mapDispatchToDynamicLayoutProps = exports.mapDispatchToDynamicControlProps = exports.mapDispatchToControlProps = exports.mapStateToControlProps = exports.oneOfToEnumOptionMapper = exports.enumToEnumOptionMapper = exports.isDescriptionHidden = exports.createDefaultValue = exports.showAsRequired = exports.computeLabel = void 0;
var tslib_1 = require("tslib");
var get_1 = tslib_1.__importDefault(require("lodash/get"));
var find_1 = tslib_1.__importDefault(require("lodash/find"));
var reducers_1 = require("../reducers");
var runtime_1 = require("./runtime");
var label_1 = require("./label");
var combinators_1 = require("./combinators");
var array_1 = require("./array");
var util_1 = require("./util");
var path_1 = require("./path");
var actions_1 = require("../actions");
var i18n_1 = require("../i18n");
var isRequired = function (schema, schemaPath, rootSchema) {
    var pathSegments = schemaPath.split('/');
    var lastSegment = pathSegments[pathSegments.length - 1];
    var nextHigherSchemaSegments = pathSegments.slice(0, pathSegments.length - 2);
    var nextHigherSchemaPath = nextHigherSchemaSegments.join('/');
    var nextHigherSchema = util_1.Resolve.schema(schema, nextHigherSchemaPath, rootSchema);
    return (nextHigherSchema !== undefined &&
        nextHigherSchema.required !== undefined &&
        nextHigherSchema.required.indexOf(lastSegment) !== -1);
};
/**
 * Adds an asterisk to the given label string based
 * on the required parameter.
 *
 * @param {string} label the label string
 * @param {boolean} required whether the label belongs to a control which is required
 * @param {boolean} hideRequiredAsterisk applied UI Schema option
 * @returns {string} the label string
 */
var computeLabel = function (label, required, hideRequiredAsterisk) {
    return required && !hideRequiredAsterisk ? label + '*' : label;
};
exports.computeLabel = computeLabel;
/**
 * Indicates whether to mark a field as required.
 *
 * @param {boolean} required whether the label belongs to a control which is required
 * @param {boolean} hideRequiredAsterisk applied UI Schema option
 * @returns {boolean} should the field be marked as required
 */
var showAsRequired = function (required, hideRequiredAsterisk) {
    return required && !hideRequiredAsterisk;
};
exports.showAsRequired = showAsRequired;
/**
 * Create a default value based on the given scheam.
 * @param schema the schema for which to create a default value.
 * @returns {any}
 */
var createDefaultValue = function (schema) {
    switch (schema.type) {
        case 'string':
            if (schema.format === 'date-time' ||
                schema.format === 'date' ||
                schema.format === 'time') {
                return new Date();
            }
            return '';
        case 'integer':
        case 'number':
            return 0;
        case 'boolean':
            return false;
        case 'array':
            return [];
        case 'null':
            return null;
        default:
            return {};
    }
};
exports.createDefaultValue = createDefaultValue;
/**
 * Whether an element's description should be hidden.
 *
 * @param visible whether an element is visible
 * @param description the element's description
 * @param isFocused whether the element is focused
 *
 * @returns {boolean} true, if the description is to be hidden, false otherwise
 */
var isDescriptionHidden = function (visible, description, isFocused, showUnfocusedDescription) {
    return (description === undefined ||
        (description !== undefined && !visible) ||
        (!showUnfocusedDescription && !isFocused));
};
exports.isDescriptionHidden = isDescriptionHidden;
var enumToEnumOptionMapper = function (e, t, i18nKey) {
    var label = typeof e === 'string' ? e : JSON.stringify(e);
    if (t) {
        if (i18nKey) {
            label = t(i18nKey + "." + label, label);
        }
        else {
            label = t(label, label);
        }
    }
    return { label: label, value: e };
};
exports.enumToEnumOptionMapper = enumToEnumOptionMapper;
var oneOfToEnumOptionMapper = function (e, t, uiSchemaI18nKey) {
    var _a;
    var label = (_a = e.title) !== null && _a !== void 0 ? _a : (typeof e.const === 'string' ? e.const : JSON.stringify(e.const));
    if (t) {
        // prefer schema keys as they can be more specialized
        if (e.i18n) {
            label = t(e.i18n, label);
        }
        else if (uiSchemaI18nKey) {
            label = t(uiSchemaI18nKey + "." + label, label);
        }
        else {
            label = t(label, label);
        }
    }
    return {
        label: label,
        value: e.const,
    };
};
exports.oneOfToEnumOptionMapper = oneOfToEnumOptionMapper;
/**
 * Map state to control props.
 * @param state the store's state
 * @param ownProps any own props
 * @returns {StatePropsOfControl} state props for a control
 */
var mapStateToControlProps = function (state, ownProps) {
    var _a, _b;
    var uischema = ownProps.uischema;
    var rootData = reducers_1.getData(state);
    var path = path_1.composeWithUi(uischema, path_1.composePaths(ownProps.path, uischema.dataPath));
    var visible = ownProps.visible === undefined || runtime_1.hasShowRule(uischema)
        ? runtime_1.isVisible(uischema, rootData, ownProps.path, reducers_1.getAjv(state))
        : ownProps.visible;
    var controlElement = uischema;
    var id = ownProps.id;
    var rootSchema = reducers_1.getSchema(state);
    var required = controlElement.scope !== undefined &&
        isRequired(ownProps.schema, controlElement.scope, rootSchema);
    var resolvedSchema = util_1.Resolve.schema(ownProps.schema || rootSchema, controlElement.scope, rootSchema);
    var errors = reducers_1.getErrorAt(path, resolvedSchema)(state);
    var description = resolvedSchema !== undefined ? resolvedSchema.description : '';
    var data = util_1.Resolve.data(rootData, path);
    var labelDesc = label_1.createLabelDescriptionFrom(uischema, resolvedSchema);
    var label = labelDesc.show ? labelDesc.text : '';
    var config = reducers_1.getConfig(state);
    var enabled = runtime_1.isInherentlyEnabled(state, ownProps, uischema, resolvedSchema || rootSchema, rootData, config);
    var schema = resolvedSchema !== null && resolvedSchema !== void 0 ? resolvedSchema : rootSchema;
    var t = reducers_1.getTranslator()(state);
    var te = reducers_1.getErrorTranslator()(state);
    var i18nLabel = t((_a = i18n_1.getI18nKey(schema, uischema, 'label')) !== null && _a !== void 0 ? _a : label, label);
    var i18nDescription = t((_b = i18n_1.getI18nKey(schema, uischema, 'description')) !== null && _b !== void 0 ? _b : description, description);
    var i18nErrorMessage = i18n_1.getCombinedErrorMessage(errors, te, t, schema, uischema);
    return {
        data: data,
        description: i18nDescription,
        errors: i18nErrorMessage,
        label: i18nLabel,
        visible: visible,
        enabled: enabled,
        id: id,
        path: path,
        required: required,
        uischema: uischema,
        schema: schema,
        config: reducers_1.getConfig(state),
        cells: ownProps.cells || state.jsonforms.cells,
        rootSchema: rootSchema
    };
};
exports.mapStateToControlProps = mapStateToControlProps;
/**
 *
 * Map dispatch to control props.
 *
 * @param dispatch the store's dispatch method
 * @returns {DispatchPropsOfControl} dispatch props for a control
 */
var mapDispatchToControlProps = function (dispatch) { return ({
    handleChange: function (path, value) {
        dispatch(actions_1.update(path, function () { return value; }));
    }
}); };
exports.mapDispatchToControlProps = mapDispatchToControlProps;
/**
 *
 * Map dispatch to dynamic-control props.
 *
 * @param dispatch the store's dispatch method
 * @returns {DispatchPropsOfControl} dispatch props for a control
 */
var mapDispatchToDynamicControlProps = function (dispatch) { return (tslib_1.__assign(tslib_1.__assign({}, exports.mapDispatchToControlProps(dispatch)), { removeThisProperty: function (path) {
        dispatch(actions_1.removeThisProperty(path));
    } })); };
exports.mapDispatchToDynamicControlProps = mapDispatchToDynamicControlProps;
/**
 * Map dispatch to dynamic-layout props.
 *
 * @param dispatch the store's dispatch method
 * @returns {DispatchPropsOfControl} dispatch props for a control
 */
var mapDispatchToDynamicLayoutProps = function (dispatch) { return ({
    addNewProperty: function (path, key, value) {
        dispatch(actions_1.addNewProperty(path, key, value));
    },
}); };
exports.mapDispatchToDynamicLayoutProps = mapDispatchToDynamicLayoutProps;
/**
 * Default mapStateToCellProps for enum control. Options is used for populating dropdown list
 * @param state
 * @param ownProps
 * @returns {StatePropsOfControl & OwnPropsOfEnum}
 */
var mapStateToEnumControlProps = function (state, ownProps) {
    var _a, _b, _c, _d;
    var props = exports.mapStateToControlProps(state, ownProps);
    var options = ownProps.options ||
        ((_a = props.schema.enum) === null || _a === void 0 ? void 0 : _a.map(function (e) {
            var _a, _b, _c;
            return exports.enumToEnumOptionMapper(e, reducers_1.getTranslator()(state), (_c = (_b = (_a = props.uischema) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.i18n) !== null && _c !== void 0 ? _c : props.schema.i18n);
        })) ||
        (props.schema.const && [
            exports.enumToEnumOptionMapper(props.schema.const, reducers_1.getTranslator()(state), (_d = (_c = (_b = props.uischema) === null || _b === void 0 ? void 0 : _b.options) === null || _c === void 0 ? void 0 : _c.i18n) !== null && _d !== void 0 ? _d : props.schema.i18n)
        ]);
    return tslib_1.__assign(tslib_1.__assign({}, props), { options: options });
};
exports.mapStateToEnumControlProps = mapStateToEnumControlProps;
/**
 * Default mapStateToCellProps for enum control based on oneOf. Options is used for populating dropdown list
 * @param state
 * @param ownProps
 * @returns {StatePropsOfControl & OwnPropsOfEnum}
 */
var mapStateToOneOfEnumControlProps = function (state, ownProps) {
    var _a;
    var props = exports.mapStateToControlProps(state, ownProps);
    var options = ownProps.options ||
        ((_a = props.schema.oneOf) === null || _a === void 0 ? void 0 : _a.map(function (oneOfSubSchema) {
            var _a, _b;
            return exports.oneOfToEnumOptionMapper(oneOfSubSchema, reducers_1.getTranslator()(state), (_b = (_a = props.uischema) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.i18n);
        }));
    return tslib_1.__assign(tslib_1.__assign({}, props), { options: options });
};
exports.mapStateToOneOfEnumControlProps = mapStateToOneOfEnumControlProps;
/**
 * Default mapStateToCellProps for multi enum control. Options is used for populating dropdown list
 * @param state
 * @param ownProps
 * @returns {StatePropsOfControl & OwnPropsOfEnum}
 */
var mapStateToMultiEnumControlProps = function (state, ownProps) {
    var _a;
    var props = exports.mapStateToControlProps(state, ownProps);
    var items = props.schema.items;
    var options = ownProps.options ||
        ((items === null || items === void 0 ? void 0 : items.oneOf) &&
            items.oneOf.map(function (oneOfSubSchema) {
                var _a, _b, _c;
                return exports.oneOfToEnumOptionMapper(oneOfSubSchema, (_a = state.jsonforms.i18n) === null || _a === void 0 ? void 0 : _a.translate, (_c = (_b = props.uischema) === null || _b === void 0 ? void 0 : _b.options) === null || _c === void 0 ? void 0 : _c.i18n);
            })) ||
        ((_a = items === null || items === void 0 ? void 0 : items.enum) === null || _a === void 0 ? void 0 : _a.map(function (e) {
            var _a, _b, _c, _d;
            return exports.enumToEnumOptionMapper(e, (_a = state.jsonforms.i18n) === null || _a === void 0 ? void 0 : _a.translate, (_d = (_c = (_b = props.uischema) === null || _b === void 0 ? void 0 : _b.options) === null || _c === void 0 ? void 0 : _c.i18n) !== null && _d !== void 0 ? _d : props.schema.i18n);
        }));
    return tslib_1.__assign(tslib_1.__assign({}, props), { options: options });
};
exports.mapStateToMultiEnumControlProps = mapStateToMultiEnumControlProps;
/**
 * Map state to control props.
 * @param state the store's state
 * @param ownProps any own props
 * @returns {StatePropsOfControl} state props for a control
 */
var mapStateToMasterListItemProps = function (state, ownProps) {
    var schema = ownProps.schema, path = ownProps.path, index = ownProps.index;
    var firstPrimitiveProp = schema.properties
        ? find_1.default(Object.keys(schema.properties), function (propName) {
            var prop = schema.properties[propName];
            return (prop.type === 'string' ||
                prop.type === 'number' ||
                prop.type === 'integer');
        })
        : undefined;
    var childPath = path_1.composePaths(path, "" + index);
    var childData = util_1.Resolve.data(reducers_1.getData(state), childPath);
    var childLabel = firstPrimitiveProp ? childData[firstPrimitiveProp] : '';
    return tslib_1.__assign(tslib_1.__assign({}, ownProps), { childLabel: childLabel });
};
exports.mapStateToMasterListItemProps = mapStateToMasterListItemProps;
/**
 * Map state to control with detail props
 *
 * @param state the store's state
 * @param ownProps any element's own props
 * @returns {StatePropsOfArrayControl} state props for a table control
 */
var mapStateToControlWithDetailProps = function (state, ownProps) {
    var props = tslib_1.__rest(exports.mapStateToControlProps(state, ownProps), []);
    return tslib_1.__assign(tslib_1.__assign({}, props), { uischemas: state.jsonforms.uischemas });
};
exports.mapStateToControlWithDetailProps = mapStateToControlWithDetailProps;
/**
 * Map state to table props
 *
 * @param state the store's state
 * @param ownProps any element's own props
 * @returns {StatePropsOfArrayControl} state props for a table control
 */
var mapStateToArrayControlProps = function (state, ownProps) {
    var _a = exports.mapStateToControlWithDetailProps(state, ownProps), path = _a.path, schema = _a.schema, uischema = _a.uischema, props = tslib_1.__rest(_a, ["path", "schema", "uischema"]);
    var resolvedSchema = util_1.Resolve.schema(schema, 'items', props.rootSchema);
    var childErrors = reducers_1.getSubErrorsAt(path, resolvedSchema)(state);
    return tslib_1.__assign(tslib_1.__assign({}, props), { path: path,
        uischema: uischema, schema: resolvedSchema, childErrors: childErrors, renderers: ownProps.renderers || reducers_1.getRenderers(state), cells: ownProps.cells || reducers_1.getCells(state) });
};
exports.mapStateToArrayControlProps = mapStateToArrayControlProps;
/**
 * Maps state to dispatch properties of an array control.
 *
 * @param dispatch the store's dispatch method
 * @returns {DispatchPropsOfArrayControl} dispatch props of an array control
 */
var mapDispatchToArrayControlProps = function (dispatch) { return ({
    addItem: function (path, value) { return function () {
        dispatch(actions_1.update(path, function (array) {
            if (array === undefined || array === null) {
                return [value];
            }
            array.push(value);
            return array;
        }));
    }; },
    removeItems: function (path, toDelete) { return function () {
        dispatch(actions_1.update(path, function (array) {
            toDelete
                .sort()
                .reverse()
                .forEach(function (s) { return array.splice(s, 1); });
            return array;
        }));
    }; },
    moveUp: function (path, toMove) { return function () {
        dispatch(actions_1.update(path, function (array) {
            array_1.moveUp(array, toMove);
            return array;
        }));
    }; },
    moveDown: function (path, toMove) { return function () {
        dispatch(actions_1.update(path, function (array) {
            array_1.moveDown(array, toMove);
            return array;
        }));
    }; }
}); };
exports.mapDispatchToArrayControlProps = mapDispatchToArrayControlProps;
var mapDispatchToMultiEnumProps = function (dispatch) { return ({
    addItem: function (path, value) {
        dispatch(actions_1.update(path, function (data) {
            if (data === undefined || data === null) {
                return [value];
            }
            data.push(value);
            return data;
        }));
    },
    removeItem: function (path, toDelete) {
        dispatch(actions_1.update(path, function (data) {
            var indexInData = data.indexOf(toDelete);
            data.splice(indexInData, 1);
            return data;
        }));
    }
}); };
exports.mapDispatchToMultiEnumProps = mapDispatchToMultiEnumProps;
exports.layoutDefaultProps = {
    visible: true,
    enabled: true,
    path: '',
    direction: 'column'
};
var getDirection = function (uischema) {
    if (uischema.type === 'HorizontalLayout') {
        return 'row';
    }
    if (uischema.type === 'VerticalLayout') {
        return 'column';
    }
    return exports.layoutDefaultProps.direction;
};
/**
 * Map state to layout props.
 * @param state JSONForms state tree
 * @param ownProps any own props
 * @returns {StatePropsOfLayout}
 */
var mapStateToLayoutProps = function (state, ownProps) {
    var _a;
    var rootData = reducers_1.getData(state);
    var uischema = ownProps.uischema;
    var visible = ownProps.visible === undefined || runtime_1.hasShowRule(uischema)
        ? runtime_1.isVisible(ownProps.uischema, rootData, ownProps.path, reducers_1.getAjv(state))
        : ownProps.visible;
    var data = util_1.Resolve.data(rootData, ownProps.path);
    var config = reducers_1.getConfig(state);
    var enabled = runtime_1.isInherentlyEnabled(state, ownProps, uischema, undefined, // layouts have no associated schema
    rootData, config);
    return tslib_1.__assign(tslib_1.__assign({}, exports.layoutDefaultProps), { renderers: ownProps.renderers || reducers_1.getRenderers(state), cells: ownProps.cells || reducers_1.getCells(state), visible: visible,
        enabled: enabled, path: ownProps.path, data: data, uischema: ownProps.uischema, schema: ownProps.schema, direction: (_a = ownProps.direction) !== null && _a !== void 0 ? _a : getDirection(uischema) });
};
exports.mapStateToLayoutProps = mapStateToLayoutProps;
var mapStateToJsonFormsRendererProps = function (state, ownProps) {
    var uischema = ownProps.uischema;
    if (uischema === undefined) {
        if (ownProps.schema) {
            uischema = reducers_1.findUISchema(state.jsonforms.uischemas, ownProps.schema, undefined, ownProps.path);
        }
        else {
            uischema = reducers_1.getUiSchema(state);
        }
    }
    return {
        renderers: ownProps.renderers || get_1.default(state.jsonforms, 'renderers') || [],
        cells: ownProps.cells || get_1.default(state.jsonforms, 'cells') || [],
        schema: ownProps.schema || reducers_1.getSchema(state),
        rootSchema: reducers_1.getSchema(state),
        uischema: uischema,
        path: ownProps.path
    };
};
exports.mapStateToJsonFormsRendererProps = mapStateToJsonFormsRendererProps;
exports.controlDefaultProps = tslib_1.__assign(tslib_1.__assign({}, exports.layoutDefaultProps), { errors: [] });
var mapStateToCombinatorRendererProps = function (state, ownProps, keyword) {
    var uischema = ownProps.uischema;
    var path = path_1.composeWithUi(uischema, ownProps.path);
    var rootSchema = reducers_1.getSchema(state);
    var resolvedSchema = util_1.Resolve.schema(ownProps.schema || rootSchema, uischema.scope, rootSchema);
    var visible = ownProps.visible === undefined || runtime_1.hasShowRule(uischema)
        ? runtime_1.isVisible(uischema, reducers_1.getData(state), ownProps.path, reducers_1.getAjv(state))
        : ownProps.visible;
    var id = ownProps.id;
    var data = util_1.Resolve.data(reducers_1.getData(state), path);
    var ajv = state.jsonforms.core.ajv;
    var schema = resolvedSchema || rootSchema;
    var _schema = combinators_1.resolveSubSchemas(schema, rootSchema, keyword);
    var structuralKeywords = [
        'required',
        'additionalProperties',
        'type',
        'enum',
        'const'
    ];
    var dataIsValid = function (errors) {
        return (!errors ||
            errors.length === 0 ||
            !errors.find(function (e) { return structuralKeywords.indexOf(e.keyword) !== -1; }));
    };
    var indexOfFittingSchema;
    // TODO instead of compiling the combinator subschemas we can compile the original schema
    // without the combinator alternatives and then revalidate and check the errors for the
    // element
    for (var i = 0; i < _schema[keyword].length; i++) {
        try {
            var valFn = ajv.compile(_schema[keyword][i]);
            valFn(data);
            if (dataIsValid(valFn.errors)) {
                indexOfFittingSchema = i;
                break;
            }
        }
        catch (error) {
            console.debug("Combinator subschema is not self contained, can't hand it over to AJV");
        }
    }
    return {
        data: data,
        path: path,
        schema: schema,
        rootSchema: rootSchema,
        visible: visible,
        id: id,
        indexOfFittingSchema: indexOfFittingSchema,
        uischemas: state.jsonforms.uischemas,
        uischema: uischema
    };
};
/**
 * Map state to all of renderer props.
 * @param state the store's state
 * @param ownProps any own props
 * @returns {StatePropsOfCombinator} state props for a combinator
 */
var mapStateToAllOfProps = function (state, ownProps) {
    return mapStateToCombinatorRendererProps(state, ownProps, 'allOf');
};
exports.mapStateToAllOfProps = mapStateToAllOfProps;
var mapStateToAnyOfProps = function (state, ownProps) {
    return mapStateToCombinatorRendererProps(state, ownProps, 'anyOf');
};
exports.mapStateToAnyOfProps = mapStateToAnyOfProps;
var mapStateToOneOfProps = function (state, ownProps) {
    return mapStateToCombinatorRendererProps(state, ownProps, 'oneOf');
};
exports.mapStateToOneOfProps = mapStateToOneOfProps;
/**
 * Map state to table props
 *
 * @param state the store's state
 * @param ownProps any element's own props
 * @returns {StatePropsOfArrayControl} state props for a table control
 */
var mapStateToArrayLayoutProps = function (state, ownProps) {
    var _a = exports.mapStateToControlWithDetailProps(state, ownProps), path = _a.path, schema = _a.schema, uischema = _a.uischema, errors = _a.errors, props = tslib_1.__rest(_a, ["path", "schema", "uischema", "errors"]);
    var resolvedSchema = util_1.Resolve.schema(schema, 'items', props.rootSchema);
    // TODO Does not consider a specialized '.custom' error message overriding all other error messages
    // TODO Does not consider 'i18n' keys which are specified in the ui schemas of the sub errors
    var childErrors = i18n_1.getCombinedErrorMessage(reducers_1.getSubErrorsAt(path, resolvedSchema)(state), reducers_1.getErrorTranslator()(state), reducers_1.getTranslator()(state), undefined, undefined);
    var allErrors = errors +
        (errors.length > 0 && childErrors.length > 0 ? '\n' : '') +
        childErrors;
    return tslib_1.__assign(tslib_1.__assign({}, props), { path: path,
        uischema: uischema, schema: resolvedSchema, data: props.data ? props.data.length : 0, errors: allErrors, minItems: schema.minItems });
};
exports.mapStateToArrayLayoutProps = mapStateToArrayLayoutProps;
//# sourceMappingURL=renderer.js.map